import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { LicensePrePaidSubFormWebComponent } from '../../../../license/licensePrePaidSubForm';
import { WEB_COMPONENT_LICENSE_PRE_PAID_SUBFORM } from '../../../../webcomponents.constants';
import { AddProductViewData } from '../types/types';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { LicenseConstants } from '../../../../license/LicenseConstants';
import { CompanyData } from '../../../types/company-data';
import { getLocalizedAsset } from '../../../../utils/i18n.utils';
import { ActionEnum } from '../types/ActionEnum';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { htmlEncode } from '../../../../utils/escape.utils';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { GzInfoBoxWebComponent } from "../../../../ui/gzInfoBox";
import { GzTextWebComponent } from '../../../../ui/gzText';

export class AddProductMenuView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $innerContainer: HTMLElement;

    $companyName: GzTitleWebComponent;
    $menuTitle: GzTitleWebComponent;

    info: {
      $text: GzTextWebComponent;
      $link: GzLinkWebComponent;
    };

    $prePaidFormTitle: GzTitleWebComponent;
    $prePaidForm: LicensePrePaidSubFormWebComponent;

    $backendErrorContainer: GzInfoBoxWebComponent;

    $addProductButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  public viewData: AddProductViewData;

  private get canManageNetworks() {
    return this.viewData.company.specifics.rights.manageNetworks;
  }

  private get canBeManagedByAbove() {
    return this.viewData.company.specifics.canBeManagedByAbove;
  }

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#add-product-wrapper'),
      $innerContainer: this.shadow.querySelector('#add-product-inner-container'),

      $companyName: this.shadow.querySelector('#add-product-company-name'),
      $menuTitle: this.shadow.querySelector('#add-product-title'),

      info: {
        $text: this.shadow.querySelector('#add-product-info-text'),
        $link: this.shadow.querySelector('#add-product-info-link'),
      },

      $prePaidFormTitle: this.shadow.querySelector('#add-product-form-title'),
      $prePaidForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_PRE_PAID_SUBFORM),

      $backendErrorContainer: this.shadow.querySelector('#add-product-backend-error-container'),

      $addProductButton: this.shadow.querySelector('#add-product-submit-button'),
      $cancelButton: this.shadow.querySelector('#add-product-cancel-button'),
    };

    this.getTranslations();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    if (this.viewData.action === ActionEnum.addProduct) {
      this.dom.$menuTitle.innerText = this.dependencies.translateFn('Add new product');
      this.dom.$addProductButton.innerText = this.dependencies.translateFn('Add product');
    } else if (this.viewData.action === ActionEnum.changeProduct) {
      this.dom.$menuTitle.innerText = this.dependencies.translateFn('Change to pre-paid license');
      this.dom.info.$text.innerText = this.dependencies.translateFn(
        'Switching to a pre-paid license will change the billing recurrence, and may lead to a change of the feature set you will have.'
      );
      this.dom.info.$link.innerHTML = `<span>${htmlEncode(
        this.dependencies.translateFn('Learn more about Bitdefender products')
      )}</span>`;
      this.dom.info.$link.href = getLocalizedAsset<string>(
        LicenseConstants.InfoLinks.CompareProducts,
        this.dependencies.getUserLanguage()
      );

      this.dom.$addProductButton.innerText = this.dependencies.translateFn('Change License');
    }

    this.dom.$prePaidFormTitle.innerText = this.dependencies.translateFn('License key');
    this.dom.$cancelButton.innerText = this.dependencies.translateFn('Cancel');
  }

  private setViewForChangeProduct() {
    toggleVisibility(this.dom.info.$text, true);
    toggleVisibility(this.dom.info.$link, true);
    this.dom.$prePaidForm.removeRequiredOnLicenseKeyField();
  }

  public setViewData(viewData: AddProductViewData) {
    // view data already set, update
    if (this.viewData) {
      this.updateViewData(viewData);
      return;
    }

    this.viewData = viewData;

    this.setCompanyNameInnerText();
    this.startFormWebComponent();
    this.setTranslatedStrings();
    if (this.viewData.action === ActionEnum.changeProduct) {
      this.setViewForChangeProduct();
    }
  }

  public updateViewData(viewData: AddProductViewData) {
    this.viewData = viewData;

    this.setVisibilityForInfoMessageForDisabledLicenseKeyField(
      this.canManageNetworks,
      this.canBeManagedByAbove,
      this.viewData.context
    );

    this.setDisabledForLicenseKeyField(!this.canManageNetworks);
    this.setDisabledForAddProductButton(!this.canManageNetworks);

    this.dom.$prePaidForm.updateViewData(this.viewData);
  }

  buildSuccessView() {}

  buildErrorView(error: Error) {}

  public setCompanyNameInnerText() {
    this.dom.$companyName.innerText = this.viewData.company.name;
  }

  private startFormWebComponent() {
    this.dom.$prePaidForm.injectDependencies(this.dependencies);
    this.dom.$prePaidForm
      .start()
      .then(() => {
        this.setupPrePaidForm();
      })
      .catch(console.error);
  }

  private setVisibilityForInfoMessageForDisabledLicenseKeyField(
    canManageNetworks: boolean,
    canBeManagedByAbove: boolean,
    context: CompanyActionContext
  ) {
    this.dom.$prePaidForm.setVisibilityForInfoMessageForDisabledLicenseKeyField(
      canManageNetworks,
      canBeManagedByAbove,
      context
    );
  }

  private setDisabledForLicenseKeyField(isDisabled: boolean) {
    this.dom.$prePaidForm?.setDisabledForLicenseKeyField(isDisabled);
  }

  private setDisabledForAddProductButton(isDisabled: boolean) {
    this.dom.$addProductButton.disabled = isDisabled;
  }

  private setupPrePaidForm() {
    this.dom.$prePaidForm.setViewData(this.viewData);
    this.dom.$prePaidForm.setCompanyType(CompanyData.getCompanyType(this.viewData.company));

    this.setVisibilityForInfoMessageForDisabledLicenseKeyField(
      this.canManageNetworks,
      this.canBeManagedByAbove,
      this.viewData.context
    );

    this.setDisabledForLicenseKeyField(!this.canManageNetworks);
    this.setDisabledForAddProductButton(!this.canManageNetworks);

    this.dom.$prePaidForm.valid$.subscribe({
      next: valid => {
        this.dom.$addProductButton.disabled =
          !valid || this.dom.$prePaidForm.hasIncompatibilityError || !this.canManageNetworks || this.dom.$prePaidForm.hasIssues;
      },
    });
  }

  public getViewData(): AddProductViewData {
    return this.viewData;
  }
}
